import axios from "axios";
import { TENANT_ID } from "../config";

const api = axios.create({
	baseURL: "https://auth.ozapgpt.com.br",
	// baseURL: "http://localhost:3002",
	headers: {
		"Content-Type": "application/json",
		"X-Tenant-ID": TENANT_ID
	},
});

/**
 * Realiza logout completo da aplicação
 * @returns {Promise<void>}
 */
const performFullLogout = async () => {
	try {
		localStorage.removeItem("authToken");

		if (typeof window !== "undefined" && (/** @type {any} */ (window)).require) {
			const { ipcRenderer } = (/** @type {any} */ (window)).require("electron");
			await ipcRenderer.invoke("disconnected_all");
		}

		window.location.reload();
	} catch (error) {
		console.error("Erro no logout completo:", error);
		window.location.reload();
	}
};

/**
 * Valida o token de autenticação do usuário
 * @returns {Promise<void>} - Uma promise que é resolvida quando o processo de validação é concluído.
 */
export const validateAuthToken = async () => {
	try {
		const token = localStorage.getItem("authToken");
		if (!token) {
			localStorage.removeItem("authToken");
			return;
		}

		const response = await api.get("/validate", {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});

		if (response.status === 200) {
			return;
		}

		if (response.status === 401 || response.status === 403) {
			console.log("Token inválido detectado. Realizando logout completo...");
			await performFullLogout();
		}
	} catch (error) {
		/** @type any */
		const errorAny = error;

		if (
			errorAny.response &&
			(errorAny.response.status === 401 || errorAny.response.status === 403)
		) {
			console.log("Acesso negado pelo servidor. Realizando logout completo...");
			await performFullLogout();
		} else {
			console.error("Erro ao verificar autenticação (mantendo usuário logado):", error);
		}
	}
};

/**
 * Busca a config de provider da oZap IA no zap-auth e envia pro processo main via IPC.
 * Silencioso: se falhar, o main usa o cache em disco / fallback.
 * @returns {Promise<void>}
 */
export const fetchOzapAiConfig = async () => {
	try {
		const token = localStorage.getItem("authToken");
		if (!token) return;

		const response = await api.get("/config/ozap-ai", {
			headers: { Authorization: `Bearer ${token}` },
		});

		const cfg = response && response.data;
		if (!cfg || !cfg.provider) return;

		if (typeof window !== "undefined" && (/** @type {any} */ (window)).require) {
			const { ipcRenderer } = (/** @type {any} */ (window)).require("electron");
			await ipcRenderer.invoke("set-ozap-ai-config", cfg);
		}
	} catch (error) {
		// 401/403 are handled by validateAuthToken's own logout path; here we just keep
		// the last-known config and stay quiet.
		console.error("Erro ao buscar config da oZap IA (mantendo cache):", error);
	}
};

/**
 * Realiza o login do usuário
 * @param {{ email: string, password: string, deviceId: string }} params - Parâmetros de login
 * @returns {Promise<any>} - Dados do usuário logado
 */
export const loginUser = async ({ email, password, deviceId }) => {
	try {
		const response = await api.post("/login", {
			email,
			password,
			deviceId,
		});
		return response.data;
	} catch (error) {
		console.error("Erro ao realizar login:", error);
		throw error;
	}
};

/**
 * Define a primeira senha do usuário
 * @param {string} newPassword - Nova senha a ser definida
 * @returns {Promise<any>} - Dados da operação
 */
export const setFirstPassword = async (newPassword) => {
	try {
		const token = localStorage.getItem("authToken");
		const response = await api.post(
			"/set-first-password",
			{ newPassword },
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		);
		return response.data;
	} catch (error) {
		console.error("Erro ao alterar a senha:", error);
		throw error;
	}
};

/**
 * Solicita o reset de senha
 * @param {string} email - Email para recuperação de senha
 * @returns {Promise<any>} - Dados da operação
 */
export const requestPasswordReset = async (email) => {
	try {
		const response = await api.post("/request-password-reset", { email });
		return response.data;
	} catch (error) {
		console.error("Erro ao solicitar código de recuperação:", error);
		throw error;
	}
};

/**
 * Redefine a senha do usuário
 * @param {{ email: string, code: string, newPassword: string }} params - Parâmetros para redefinição de senha
 * @returns {Promise<any>} - Dados da operação
 */
export const resetPassword = async ({ email, code, newPassword }) => {
	try {
		const response = await api.post("/reset-password", {
			email,
			code,
			newPassword,
		});
		return response.data;
	} catch (error) {
		console.error("Erro ao redefinir senha:", error);
		throw error;
	}
};
